import clsx from "clsx";
import { message } from "antd";
import { useState } from "react";
import { useApi } from "../Api/useApi.ts";

import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { newFormProps } from "../Constants/newForm.tsx";
import { useFormEditorContext } from "../Contexts/FormEditorContext";
import {
    convertBackToOldFormat,
    convertFormObject,
} from "./MyEditor/FormObjectConfig";

import { Button } from "./ui/button.tsx";
import { Textarea } from "./ui/textarea.tsx";
import { Separator } from "./ui/separator.tsx";
import { Input as ShadInput } from "./ui/input.tsx";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs.tsx";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "./ui/dialog.tsx";

import { FilePenLine, LoaderCircle, Sparkles } from "lucide-react";

interface FormCreateVariables {
    data: any;
    workspaceId: string;
}
interface DynamicFormCreateVariables {
    name: string;
    description: string;
    workspaceId: string;
}

export const FormCreateModal = ({ createModalOpen, setCreateModalOpen }) => {
    const { selectedWorkSpace, setForm } = useFormEditorContext();
    const api = useApi();
    const navigate = useNavigate();
    const [dynamicName, setDynamicName] = useState("");
    const [guidedName, setGuidedName] = useState("");
    const [error, setError] = useState("");
    const [dynamicDescription, setDynamicDescription] = useState("");
    const [isCreatingGeneralForm, setIsCreatingGeneralForm] = useState(false);
    const [isCreatingDynamicForm, setIsCreatingDynamicForm] = useState(false);

    const formCreateMutation = useMutation<any, unknown, FormCreateVariables>({
        mutationFn: ({ data, workspaceId }) =>
            //@ts-ignore
            api.revisions.createRevision(data, workspaceId),
        onSuccess: (data) => {
            const newObj = convertBackToOldFormat(data?.data?.form);
            const formId = data?.data?.form?._id;
            setForm(newObj?.form);
            sessionStorage.setItem("selectedWorkSpace", selectedWorkSpace);
            sessionStorage.setItem("formStatus", "draft");
            setIsCreatingGeneralForm(false);
            navigate(`/workspaces/${selectedWorkSpace}/form-editor/${formId}`);
            setCreateModalOpen(false);
        },
        onError: (error) => {
            if (
                typeof error === "object" &&
                error !== null &&
                "response" in error
            ) {
                const err = error as { response?: any };
                console.log(err?.response);
                message.error(
                    err?.response?.data?.detail || "Something went wrong",
                );
            }
            console.error("Error details:", error);
            setIsCreatingGeneralForm(false);
            setCreateModalOpen(false);
        },
    });

    const dynamicFormCreateMutation = useMutation<
        any,
        unknown,
        DynamicFormCreateVariables
    >({
        mutationFn: ({ name, description, workspaceId }) => {
            // Ensure createAiForms returns a promise
            return new Promise((resolve, reject) => {
                api.aiForms
                    .createAiForms(name, description, workspaceId)
                    .then((response) => {
                        resolve(response);
                        //console.log(response);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },
        onSuccess: (data) => {
            const newObj = convertBackToOldFormat(data?.data?.form);
            setForm(newObj?.form);
            sessionStorage.setItem("selectedWorkSpace", selectedWorkSpace);
            sessionStorage.setItem("formStatus", "draft");
            navigate(
                `/workspaces/${selectedWorkSpace}/form-editor/${data?.data?.form?._id}`,
            );
            setIsCreatingDynamicForm(false);
            setCreateModalOpen(false);
        },
        onError: (error) => {
            console.error("Mutation error:", error);
            setIsCreatingDynamicForm(false);
        },
    });

    const handleCreateNewForm = () => {
        if (!guidedName) {
            setError("Fields can't be empty");
            return;
        }
        if (!selectedWorkSpace) {
            message.error("Please select a workspace");
            return;
        }
        setIsCreatingGeneralForm(true);
        const newForm = newFormProps(guidedName);
        const newFormat = convertFormObject(newForm);
        formCreateMutation.mutate({
            data: newFormat,
            workspaceId: selectedWorkSpace,
        });
    };

    const handleDynamicFormCreation = () => {
        if (!dynamicName) {
            setError("Fields can't be empty");
            return;
        }
        if (!selectedWorkSpace) {
            message.error("Please select a workspace");
            return;
        }

        setIsCreatingDynamicForm(true);
        dynamicFormCreateMutation.mutate({
            name: dynamicName,
            description: dynamicDescription,
            workspaceId: selectedWorkSpace,
        });
    };

    return (
        <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
            <DialogContent
                className={clsx(
                    "p-0 rounded-[8px] top-[40%] z-[9999] w-[460px]",
                    isCreatingDynamicForm && "dialog-border-animation",
                )}
            >
                <DialogHeader className=" px-3 pt-3 space-y-0.5">
                    <DialogTitle className="flex items-center gap-1">
                        <FilePenLine size={14} />
                        <span className="text-sm font-medium text-primary">
                            Create New Form
                        </span>
                    </DialogTitle>
                    <DialogDescription className="text-xs text-primary">
                        Get a head start with BuildForm AI or create your form
                        from scratch
                    </DialogDescription>
                </DialogHeader>
                <Separator />

                <Tabs defaultValue="dynamic" className="w-full px-3">
                    <TabsList className="w-full h-9">
                        <TabsTrigger
                            value="dynamic"
                            className="w-full text-xs flex items-center gap-1 text-[#44546F]"
                        >
                            BuildForm AI
                            <Sparkles size={14} className="font-normal" />
                        </TabsTrigger>
                        <TabsTrigger
                            value="guided"
                            className="w-full text-xs flex items-center gap-1 text-[#44546F]"
                        >
                            Start from Scratch
                        </TabsTrigger>
                    </TabsList>
                    <TabsContent value="dynamic">
                        <div className="mt-2 mb-4">
                            <div className="rounded-[8px] w-full bg-blue-50 flex p-2 gap-1.5 items-start">
                                <svg
                                    width="14"
                                    height="15"
                                    viewBox="0 0 14 15"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M7.00008 9.64616V7.31283M7.00008 4.97949H7.00591M12.8334 7.31283C12.8334 10.5345 10.2217 13.1462 7.00008 13.1462C3.77842 13.1462 1.16675 10.5345 1.16675 7.31283C1.16675 4.09116 3.77842 1.47949 7.00008 1.47949C10.2217 1.47949 12.8334 4.09116 12.8334 7.31283Z"
                                        stroke="#2563EB"
                                        stroke-width="0.875"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    />
                                </svg>

                                <p className="text-xs text-blue-600">
                                    For better results provide clear details,
                                    audience, and context
                                </p>
                            </div>
                        </div>
                        <div className="flex flex-col gap-4 mb-4">
                            <div className="flex flex-col">
                                <p className="text-xs font-medium text-primary">
                                    Form name
                                </p>
                                <p className="text-xs text-zinc-500">
                                    The form name will be displayed when sharing
                                </p>
                                <ShadInput
                                    className="flex w-full h-10 px-3 py-2 mt-1 text-xs transition duration-150 bg-white border rounded-lg border-zinc-200 file:border-0 file:bg-transparent file:text-sm file:font-medium focus-visible:outline-none focus-visible:ring-0 disabled:cursor-not-allowed disabled:opacity-50 placeholder:text-zinc-500"
                                    required
                                    placeholder="e.g. Feedback form"
                                    onChange={(e) => {
                                        setError("");
                                        setDynamicName(e.target.value);
                                    }}
                                    autoFocus
                                />
                            </div>

                            <div className="flex flex-col">
                                <p className="text-xs font-medium text-primary">
                                    Form description
                                </p>
                                <p className="text-xs text-zinc-500">
                                    Describe the goal of this form
                                </p>
                                <Textarea
                                    rows={1}
                                    className="flex w-full h-10 px-3 py-2 mt-1 text-xs transition duration-150 bg-white border rounded-lg resize-none border-zinc-200 file:border-0 file:bg-transparent file:text-sm file:font-medium focus-visible:outline-none focus-visible:ring-0 disabled:cursor-not-allowed disabled:opacity-50 placeholder:text-zinc-500 placeholder:text-xs"
                                    placeholder="e.g. A form to collect feedback from my customers"
                                    onChange={(e) => {
                                        setError("");
                                        setDynamicDescription(e.target.value);
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            handleDynamicFormCreation();
                                        }
                                    }}
                                />
                                {error && (
                                    <p className="pl-1 mt-1 text-xs text-red-500">
                                        {error}
                                    </p>
                                )}
                            </div>
                        </div>
                        {/* button section */}
                        <div className="flex flex-col gap-2 mb-4">
                            <Button
                                className="h-9 text-white rounded-[8px] py-1.5 bg-primary hover:bg-primary/90"
                                onClick={handleDynamicFormCreation}
                                type="submit"
                                disabled={isCreatingDynamicForm}
                            >
                                {isCreatingDynamicForm ? (
                                    <div className="flex gap-1.5 items-center">
                                        <span className="bg-white rounded-full size-3"></span>
                                        <span className="text-sm">
                                            Buildform AI is thinking...
                                        </span>
                                    </div>
                                ) : (
                                    "          Build a New Form"
                                )}
                            </Button>
                            <Button
                                className="bg-white h-9 rounded-[8px]  text-primary border-[#DCDFE4] border hover:bg-white"
                                onClick={() => {
                                    setCreateModalOpen(false);
                                }}
                            >
                                Cancel
                            </Button>
                        </div>
                    </TabsContent>
                    <TabsContent value="guided">
                        <div className="mt-2 mb-4">
                            <div className="rounded-[8px] w-full bg-blue-50 flex p-2 gap-1.5 items-start">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                    aria-hidden="true"
                                    data-slot="icon"
                                    className="h-[0.8rem] min-w-[0.8rem] text-blue-600 mt-0.5"
                                >
                                    <path d="M10 1a6 6 0 0 0-3.815 10.631C7.237 12.5 8 13.443 8 14.456v.644a.75.75 0 0 0 .572.729 6.016 6.016 0 0 0 2.856 0A.75.75 0 0 0 12 15.1v-.644c0-1.013.762-1.957 1.815-2.825A6 6 0 0 0 10 1ZM8.863 17.414a.75.75 0 0 0-.226 1.483 9.066 9.066 0 0 0 2.726 0 .75.75 0 0 0-.226-1.483 7.553 7.553 0 0 1-2.274 0Z"></path>
                                </svg>
                                <p className="text-xs text-blue-600">
                                    AI statements are available for Start from
                                    Scratch Projects
                                </p>
                            </div>
                        </div>
                        <div className="flex flex-col gap-4 mb-4">
                            <div className="flex flex-col">
                                <p className="text-xs font-medium text-primary">
                                    Form name
                                </p>
                                <p className="text-xs text-zinc-500">
                                    The form name will be displayed when sharing
                                </p>
                                <ShadInput
                                    className="flex w-full h-10 px-3 py-2 mt-1 text-xs transition duration-150 bg-white border rounded-lg border-zinc-200 file:border-0 file:bg-transparent file:text-sm file:font-medium focus-visible:outline-none focus-visible:ring-0 disabled:cursor-not-allowed disabled:opacity-50 placeholder:text-zinc-500"
                                    required
                                    placeholder="e.g. Feedback form"
                                    onChange={(e) => {
                                        setError("");
                                        setGuidedName(e.target.value);
                                    }}
                                    autoFocus
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            handleCreateNewForm();
                                        }
                                    }}
                                />
                                {error && (
                                    <p className="pl-1 mt-1 text-xs text-red-500">
                                        {error}
                                    </p>
                                )}
                            </div>
                        </div>
                        {/* button section */}
                        <div className="flex flex-col gap-2 mb-4">
                            <Button
                                className="h-9 text-white rounded-[8px] py-1.5 bg-primary hover:bg-primary/90"
                                onClick={handleCreateNewForm}
                                disabled={isCreatingGeneralForm}
                            >
                                {isCreatingGeneralForm ? (
                                    <LoaderCircle className="animate-spin" />
                                ) : (
                                    <span>Build a New Form</span>
                                )}
                            </Button>
                            <Button
                                className="bg-white h-9 rounded-[8px]  text-primary border-[#DCDFE4] border hover:bg-white"
                                onClick={() => {
                                    setCreateModalOpen(false);
                                }}
                            >
                                Cancel
                            </Button>
                        </div>
                    </TabsContent>
                </Tabs>
            </DialogContent>
        </Dialog>
    );
};
